import React from "react";
import { Outlet } from "react-router-dom";
import styled from "styled-components";

/**
 * `height` (not `min-height`) — a real ceiling, not just a floor. Every other route here still
 * overflows/scrolls at the page level exactly as before (nothing here clips — `overflow` stays
 * the `visible` default, so taller-than-viewport content still paints past this box and the
 * document still scrolls for it); the only thing a definite `height` changes is that `Content`
 * below now has an actual bound to flex-shrink against instead of an infinite floor, which is
 * what lets a route opt into "fill the viewport, scroll internally" instead (see
 * `CandidateAssignmentsScreen.tsx`'s own fill-chain doc comment for the one route that does).
 */
// Same literal `#0a0b0d` as `ShellHeader`'s own `Bar` — Addison: "make the body the same color
// as the header... everything's going to be floating on top of that" (2026-09-08 interactive
// session). Page content no longer sits directly on this background at all; it floats in its own
// `<Section>` card instead, so the visual depth/contrast now comes from the Section's own
// lighter `--dark100` background against this, not from a body-vs-header color step. A literal,
// not a `--dark*` token, for the same reason `Bar` uses one: `--dark060` was already the darkest
// step in either palette this app uses, so matching the header needs a value below that floor.
const Shell = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #0a0b0d;
`;

const Content = styled.main`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/**
 * Customer stack layout shell — just the routing outlet + the shared dark background. Header
 * chrome lives one level further in, at `ShellLayout` (mounted for every real route, `ab-vault`/
 * `spotter` included — see `AppRouter.tsx`), not here, so this component stays a plain wrapper.
 *
 * No customer iconik logo anywhere in the app (the dead `customerLogoUrl` field that used to be
 * wired through the `GET /api/context` contract/backend for this was removed entirely — see
 * `useCustomerContext.ts`'s `CustomerContext` type and `apps/customer-api/src/routes/context.ts`).
 */
const CustomerLayout = () => (
  <Shell>
    <Content>
      <Outlet />
    </Content>
  </Shell>
);

export default CustomerLayout;
